/**
 * CommentPopover - Shows comment thread when clicking highlighted text
 *
 * This component displays when a user clicks on text that has a comment.
 * It shows the comment thread (root + replies) and allows:
 * - Viewing all comments in the thread
 * - Adding new replies
 * - Resolving the thread (removes highlight from editor)
 *
 * How it works:
 * 1. User clicks highlighted text in editor
 * 2. Comment extension calls onCommentClick with commentId and rect
 * 3. Parent component renders CommentPopover at the rect position
 * 4. Popover fetches thread from GET /api/comments/:commentId
 * 5. User can reply or resolve
 * 6. On resolve, parent calls editor.commands.unsetComment(commentId)
 *
 * Requirements implemented:
 * - RICH-08: Inline comments on text selections
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Comment data structure (from API)
 *
 * Maps database columns to camelCase properties
 */
interface Comment {
  /** Unique comment ID */
  id: string;
  /** Thread root ID (for grouping) */
  threadId: string;
  /** Parent comment ID (null for root) */
  parentId: string | null;
  /** Author's user ID */
  authorId: string;
  /** Author's display name */
  authorName: string;
  /** Comment text content */
  content: string;
  /** Resolution timestamp (null if unresolved) */
  resolvedAt: number | null;
  /** Unix timestamp when created */
  createdAt: number;
}

/**
 * Props for CommentPopover component
 */
interface CommentPopoverProps {
  /** ID of the comment (thread root) to display */
  commentId: string;

  /** Board ID for creating replies */
  boardId: string;

  /** Position for the popover (from getBoundingClientRect) */
  position: { top: number; left: number };

  /** Callback when popover should close */
  onClose: () => void;

  /**
   * Callback when thread is resolved.
   * Parent should call editor.commands.unsetComment(commentId) to remove mark.
   */
  onResolve: (commentId: string) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * CommentPopover - Thread display and reply UI
 *
 * @example
 * {activeComment && (
 *   <CommentPopover
 *     commentId={activeComment.id}
 *     boardId={boardId}
 *     position={activeComment.position}
 *     onClose={() => setActiveComment(null)}
 *     onResolve={(id) => {
 *       editor?.commands.unsetComment(id);
 *     }}
 *   />
 * )}
 */
export function CommentPopover({
  commentId,
  boardId,
  position,
  onClose,
  onResolve,
}: CommentPopoverProps) {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  // Comment thread data
  const [comments, setComments] = useState<Comment[]>([]);
  // Loading state for initial fetch
  const [loading, setLoading] = useState(true);
  // Reply input text
  const [replyText, setReplyText] = useState('');
  // Submitting reply state
  const [isReplying, setIsReplying] = useState(false);

  // Ref for click-outside detection
  const popoverRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // FETCH COMMENT THREAD
  // ---------------------------------------------------------------------------

  useEffect(() => {
    /**
     * Fetch the comment thread from API.
     * Returns the root comment and all replies.
     */
    const fetchComments = async () => {
      try {
        const response = await fetch(`/api/comments/${commentId}`, {
          credentials: 'include', // Include session cookie
        });

        if (response.ok) {
          const data = await response.json();

          // Map API response (snake_case) to state (camelCase)
          setComments(
            data.data.map((c: any) => ({
              id: c.id,
              threadId: c.thread_id,
              parentId: c.parent_id,
              authorId: c.author_id,
              authorName: c.author_name,
              content: c.content,
              resolvedAt: c.resolved_at,
              createdAt: c.created_at,
            }))
          );
        } else {
          console.error('[CommentPopover] Failed to fetch:', response.status);
        }
      } catch (error) {
        console.error('[CommentPopover] Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [commentId]);

  // ---------------------------------------------------------------------------
  // CLICK OUTSIDE HANDLER
  // ---------------------------------------------------------------------------

  useEffect(() => {
    /**
     * Close popover when clicking outside.
     * This provides expected UX of dismissing by clicking away.
     */
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    // Use mousedown to catch clicks before they propagate
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Submit a reply to the comment thread.
   * Creates a new comment with parentId pointing to thread root.
   */
  const handleReply = useCallback(async () => {
    // Validate input
    if (!replyText.trim()) return;

    setIsReplying(true);

    try {
      const response = await fetch('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          boardId,
          content: replyText,
          parentId: commentId, // Reply to the thread root
        }),
      });

      if (response.ok) {
        const data = await response.json();

        // Add new reply to local state
        setComments((prev) => [
          ...prev,
          {
            id: data.data.id,
            threadId: data.data.threadId,
            parentId: data.data.parentId,
            authorId: data.data.authorId,
            authorName: data.data.authorName,
            content: data.data.content,
            resolvedAt: null,
            createdAt: data.data.createdAt,
          },
        ]);

        // Clear input
        setReplyText('');
      } else {
        console.error('[CommentPopover] Reply failed:', response.status);
      }
    } catch (error) {
      console.error('[CommentPopover] Reply error:', error);
    } finally {
      setIsReplying(false);
    }
  }, [boardId, commentId, replyText]);

  /**
   * Resolve the comment thread.
   * Marks all comments in thread as resolved and notifies parent.
   */
  const handleResolve = useCallback(async () => {
    try {
      const response = await fetch(`/api/comments/${commentId}/resolve`, {
        method: 'PATCH',
        credentials: 'include',
      });

      if (response.ok) {
        // Notify parent to remove the mark from editor
        onResolve(commentId);
        // Close popover
        onClose();
      } else {
        console.error('[CommentPopover] Resolve failed:', response.status);
      }
    } catch (error) {
      console.error('[CommentPopover] Resolve error:', error);
    }
  }, [commentId, onResolve, onClose]);

  // ---------------------------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------------------------

  /**
   * Format Unix timestamp to human-readable date/time.
   * Example: "Jan 20, 10:30 AM"
   */
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div
      ref={popoverRef}
      className="comment-popover"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
      }}
    >
      {loading ? (
        // Loading state
        <div className="comment-popover-loading">Loading...</div>
      ) : (
        <>
          {/* ---------------------------------------------------------------- */}
          {/* COMMENT THREAD */}
          {/* ---------------------------------------------------------------- */}
          <div className="comment-popover-thread">
            {comments.map((comment) => (
              <div key={comment.id} className="comment-popover-item">
                {/* Author and timestamp */}
                <div className="comment-popover-header">
                  <span className="comment-popover-author">
                    {comment.authorName}
                  </span>
                  <span className="comment-popover-time">
                    {formatTime(comment.createdAt)}
                  </span>
                </div>

                {/* Comment content */}
                <p className="comment-popover-content">{comment.content}</p>
              </div>
            ))}
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* REPLY FORM */}
          {/* ---------------------------------------------------------------- */}
          <div className="comment-popover-reply">
            {/* Reply textarea */}
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Reply..."
              className="comment-popover-textarea"
              rows={2}
            />

            {/* Action buttons */}
            <div className="comment-popover-actions">
              {/* Resolve button - marks thread as resolved, removes highlight */}
              <button
                type="button"
                onClick={handleResolve}
                className="comment-popover-button secondary"
              >
                Resolve
              </button>

              {/* Reply button - adds reply to thread */}
              <button
                type="button"
                onClick={handleReply}
                disabled={!replyText.trim() || isReplying}
                className="comment-popover-button primary"
              >
                {isReplying ? 'Sending...' : 'Reply'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
